import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import {
  CreditCard,
  Palette,
  Download,
  Shield,
  Zap,
  Globe,
  ArrowRight,
  User,
  LogIn,
  Menu,
  X,
} from "lucide-react";

const HomePage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(true);

  React.useEffect(() => {
    const tierOrder = { signature: 1, atelier: 2, executive: 3 };
    const tierTone = {
      signature: "border-emerald-200",
      atelier: "border-blue-300 ring-1 ring-blue-200",
      executive: "border-amber-200",
    };
    fetch("/api/products")
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        const mapped = list
          .filter((p) => p && p.tier && tierOrder[p.tier])
          .sort((a, b) => tierOrder[a.tier] - tierOrder[b.tier])
          .map((p) => {
            const tierName = p.tier.charAt(0).toUpperCase() + p.tier.slice(1);
            const qty = p.specifications?.quantity || p.quantity;
            const paper = p.specifications?.paper || "";
            const paperShort = paper
              .replace("premium cotton cardstock", "premium cotton")
              .replace("double-thick duplex (white core)", "double-thick + UV")
              .replace(/cardstock.*/i, "")
              .trim();
            const detail = qty
              ? `${qty.toLocaleString()} cards${paperShort ? " · " + paperShort : ""}`
              : paperShort;
            return {
              id: p.id || p._id,
              name: tierName,
              price: typeof p.basePrice === "number" ? `$${p.basePrice}` : "—",
              detail: detail || "—",
              tone: tierTone[p.tier] || "border-gray-200",
            };
          });
        setTiers(mapped);
        setTiersLoading(false);
      })
      .catch(() => setTiersLoading(false));
  }, []);

  const features = [
    {
      icon: <CreditCard className="h-7 w-7 sm:h-8 sm:w-8 text-emerald-600" />,
      title: "Atelier Quality",
      description:
        "32pt cotton to 48pt letterpress. Premium stock, hand-finished. Materials worthy of your name.",
    },
    {
      icon: <Palette className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600" />,
      title: "Curated Design Catalog",
      description:
        "Hand-picked templates by industry. Customizable end to end, or fully bespoke.",
    },
    {
      icon: <Globe className="h-7 w-7 sm:h-8 sm:w-8 text-purple-600" />,
      title: "3D Preview",
      description:
        "See your card in stunning 3D before printing. No surprises, no waste.",
    },
    {
      icon: <Download className="h-7 w-7 sm:h-8 sm:w-8 text-orange-600" />,
      title: "Print-Ready PDF",
      description:
        "300 DPI, CMYK, full bleed. Files sent straight to professional print.",
    },
    {
      icon: <Shield className="h-7 w-7 sm:h-8 sm:w-8 text-green-600" />,
      title: "NFC + QR vCard",
      description:
        "Optional NFC chip and built-in QR. Share your contact in one tap.",
    },
    {
      icon: <Zap className="h-7 w-7 sm:h-8 sm:w-8 text-red-600" />,
      title: "Wunli AI Brand Assistant",
      description:
        "AI suggests slogan, tone and visual identity matched to your industry.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 min-w-0">
              <div className="bg-gradient-to-r from-emerald-500 to-blue-600 p-2 rounded-lg flex-shrink-0">
                <CreditCard className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <span className="text-base sm:text-xl font-bold text-gray-900 truncate">
                Wunli Print Studio
              </span>
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center space-x-2 lg:space-x-4">
              <Link to="/login">
                <Button
                  variant="ghost"
                  className="text-gray-600 hover:text-gray-900"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Customer Login
                </Button>
              </Link>
              <Link to="/admin/login">
                <Button
                  variant="ghost"
                  className="text-gray-600 hover:text-gray-900"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Admin
                </Button>
              </Link>
            </div>

            {/* Mobile menu toggle */}
            <button
              type="button"
              aria-label="Toggle menu"
              className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>

          {/* Mobile drawer */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-3 pb-2 border-t border-gray-200 pt-3 space-y-2">
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center px-3 py-2 rounded-md text-gray-700 hover:bg-gray-50"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Customer Login
              </Link>
              <Link
                to="/admin/login"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center px-3 py-2 rounded-md text-gray-700 hover:bg-gray-50"
              >
                <Shield className="h-4 w-4 mr-2" />
                Admin Login
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-12 sm:pt-16 md:pt-20 pb-16 sm:pb-24 md:pb-32">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-blue-600/10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm rounded-full px-3 sm:px-4 py-2 mb-6 sm:mb-8 border border-gray-200">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
              <span className="text-xs sm:text-sm font-medium text-gray-700">
                Atelier business cards · Hand-finished
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 sm:mb-8 leading-tight tracking-tight">
              Premium Cards For
              <span className="bg-gradient-to-r from-emerald-500 to-blue-600 bg-clip-text text-transparent block">
                Founders & Executives
              </span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed px-2">
              Wunli Print Studio crafts atelier-grade business cards — 32pt
              cotton, double-thick stock, spot UV and foil. Designed on your
              phone, printed by a professional press.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
              <Link to="/signup" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  <User className="mr-2 h-5 w-5" />
                  Start your atelier card
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/showcase" className="w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg border-2 hover:bg-gray-50"
                >
                  Browse the collection
                </Button>
              </Link>
            </div>

            <div className="mt-6 sm:mt-8 text-xs sm:text-sm text-gray-500">
              <p>
                Atelier quality · Print-ready PDF · Fast professional delivery
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 sm:py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-14 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Everything The Atelier Includes
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-2">
              Professional tools to design, preview and deliver a card that
              opens doors.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 md:gap-8">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="group hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-white to-gray-50/50 hover:from-white hover:to-gray-100/50"
              >
                <CardContent className="p-6 sm:p-7 md:p-8">
                  <div className="mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Benefits + Tiers */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-5 sm:mb-6">
                Why Wunli Print Studio?
              </h2>
              <div className="space-y-5 sm:space-y-6">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="bg-emerald-100 p-2 rounded-lg flex-shrink-0">
                    <User className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-base sm:text-lg">
                      Atelier-grade materials
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      32pt cotton, 38pt double-thick, 48pt letterpress. Always
                      printed on premium stock — never desktop paper.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                    <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-base sm:text-lg">
                      Secure checkout
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      256-bit encrypted Stripe payment. PCI-compliant. Receipt
                      and order confirmation in your inbox in seconds.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="bg-purple-100 p-2 rounded-lg flex-shrink-0">
                    <Download className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-base sm:text-lg">
                      Print-perfect output
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      Print-ready PDFs (3.5" × 2", 300 DPI, full bleed) sent to
                      a professional press, not a desktop printer.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-500 to-blue-600 rounded-2xl p-6 sm:p-8 text-white">
              <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-5">
                Three Atelier Tiers
              </h3>
              <div className="space-y-3 mb-6">
                {tiersLoading ? (
                  [0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 border-2 border-white/20 flex items-center justify-between animate-pulse"
                    >
                      <div className="min-w-0 space-y-2">
                        <div className="h-4 w-24 bg-white/30 rounded"></div>
                        <div className="h-3 w-36 bg-white/20 rounded"></div>
                      </div>
                      <div className="h-5 w-12 bg-white/30 rounded flex-shrink-0 pl-3"></div>
                    </div>
                  ))
                ) : tiers.length === 0 ? (
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-sm text-white/80 text-center">
                    Pricing temporarily unavailable. Please contact us directly.
                  </div>
                ) : (
                  tiers.map((tier) => (
                    <div
                      key={tier.id}
                      className={`bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 border-2 ${tier.tone} flex items-center justify-between`}
                    >
                      <div className="min-w-0">
                        <div className="font-semibold text-base sm:text-lg">
                          {tier.name}
                        </div>
                        <div className="text-xs sm:text-sm opacity-90 truncate">
                          {tier.detail}
                        </div>
                      </div>
                      <div className="font-bold text-lg sm:text-xl flex-shrink-0 pl-3">
                        {tier.price}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="space-y-3">
                <Link to="/showcase" className="block">
                  <Button
                    size="lg"
                    className="w-full bg-white text-emerald-600 hover:bg-gray-50 font-semibold"
                  >
                    Browse the collection
                  </Button>
                </Link>
                <Link to="/signup" className="block">
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full border-white text-white hover:bg-white/10"
                  >
                    Start a free account
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-r from-emerald-500 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            Build the card that opens doors
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-emerald-100 mb-6 sm:mb-8 leading-relaxed px-2">
            Atelier-grade business cards delivered fast. Design on your phone,
            print at a professional press.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link to="/signup" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-white text-emerald-600 hover:bg-gray-50 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Start your atelier card
              </Button>
            </Link>
            <Link to="/login" className="w-full sm:w-auto">
              <Button
                variant="outline"
                size="lg"
                className="w-full sm:w-auto border-white text-white hover:bg-white/10 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg"
              >
                I already have an account
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-10 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center space-x-2 mb-6 sm:mb-8">
            <div className="bg-gradient-to-r from-emerald-500 to-blue-600 p-2 rounded-lg">
              <CreditCard className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <span className="text-lg sm:text-xl font-bold">
              Wunli Print Studio
            </span>
          </div>
          <div className="text-center text-gray-400 text-sm sm:text-base">
            <p>&copy; 2026 Wunli Digital Marketing Solutions. All rights reserved.</p>
            <p className="mt-2 px-4">
              Atelier-grade business cards. Hand-finished. Print-perfect.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
